import { createContext, useContext, useEffect, useState } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { setUser as setSentryUser } from '@/lib/sentry'
import * as storage from '@/lib/safeStorage'
import type { Profile } from '@/types'

interface AuthContextValue {
  user: User | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  signOut: () => Promise<void>
  refetchProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Best-effort: convert any pending org_invitations for this email into
    // real memberships. Fire-and-forget so a slow RPC (e.g. cold Supabase
    // Free project) can't block first render on "Loading...". When it
    // resolves, invalidate memberships so OrgContext picks up any new ones.
    function acceptInvitationsInBackground(userId: string) {
      supabase.rpc('accept_my_invitations').then(
        () => {
          queryClient.invalidateQueries({ queryKey: ['memberships', userId] })
        },
        () => {},
      )
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email ?? undefined })
        fetchProfile(session.user.id)
        acceptInvitationsInBackground(session.user.id)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
        if (session?.user) {
          setSentryUser({ id: session.user.id, email: session.user.email ?? undefined })
          fetchProfile(session.user.id)
          acceptInvitationsInBackground(session.user.id)
        } else {
          setSentryUser(null)
          setProfile(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [queryClient])

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    setProfile(data ?? null)
  }

  async function signOut() {
    // Clear per-session selections so the next login starts fresh
    // (forces the org picker to re-appear when the user has 2+ orgs).
    storage.removeItem('tourflow-selected-org')
    storage.removeItem('tourflow-selected-tour')
    await supabase.auth.signOut()
  }

  async function refetchProfile() {
    if (user?.id) await fetchProfile(user.id)
  }

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signOut, refetchProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
